import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Form, FormControl, FormGroup, InputGroup } from 'react-bootstrap';

import UrlBuilderService from '../../services/UrlBuilderService';
import { useUserContext } from 'App/contexts/UserContext';
import isPermitted, { PERMISSION_TYPES } from 'App/utils/isPermitted';

const SEARCH_TYPES = {
    LAST_NAME: 'lastName',
    ID: 'id',
    PHONE: 'phone',
};

const SEARCH_TYPE_OPTIONS = [
    { value: SEARCH_TYPES.LAST_NAME, label: 'Last Name' },
    { value: SEARCH_TYPES.ID, label: 'ID' },
    { value: SEARCH_TYPES.PHONE, label: 'Phone' },
];

const SEARCH_TYPE_PLACEHOLDERS = {
    [SEARCH_TYPES.LAST_NAME]: 'Find patients by last name',
    [SEARCH_TYPES.ID]: 'Find patients by ID',
    [SEARCH_TYPES.PHONE]: 'Find patients by phone',
};

// Parses free-text name input into first/last name query params
const buildNameQueryObj = (searchName) => {
    const queryObj = {};
    const names = searchName.split(/[ ,]+/); // Split by any amount of whitespace or commas

    if (searchName.indexOf(',') !== -1) {
        const [last = '', first = ''] = searchName.split(',');
        queryObj.firstName = first.trim();
        queryObj.lastName = last.trim();
    } else if (names.length === 1) {
        queryObj.lastName = searchName;
    } else {
        // to handle the name in <first last last> format
        const { groups } = searchName.match(/^(?<prefix>Dr\.? )?(?:(?<firstName>\w+) )?(?<lastName>.*)$/i);
        queryObj.firstName = groups.firstName || '';
        queryObj.lastName = (groups.prefix || '') + groups.lastName;
    }
    return queryObj;
};

const SearchBar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchValue, setSearchValue] = useState('');
    const [searchType, setSearchType] = useState(SEARCH_TYPES.LAST_NAME);
    const user = useUserContext();
    // PDX-enabled companies don't have ID/Phone fields on their Find Patient page, so keep them on name search only.
    const isPdxUser = isPermitted(PERMISSION_TYPES.PDX_FIND_PATIENT, user);

    const onSearchValue = (event) => {
        if (event && event.target) {
            const { value } = event.target;
            setSearchValue(value);
        }
    };

    const onSearchTypeChange = (event) => {
        if (event && event.target) {
            setSearchType(event.target.value);
            setSearchValue('');
        }
    };

    const searchPatient = (event) => {
        // Call event.preventDefault() here to prevent event from bubbling up
        event.preventDefault();
        const trimmedValue = searchValue.trim();
        if (trimmedValue) {
            let queryObj;

            if (searchType === SEARCH_TYPES.ID) {
                // The DISPLAYED_ID -> field mapping (including EHR type lookup) is resolved in UpdateSearchParams.
                queryObj = { id: trimmedValue };
            } else if (searchType === SEARCH_TYPES.PHONE) {
                const digitsOnly = trimmedValue.replace(/\D/g, '');
                if (!digitsOnly) {
                    return;
                }
                queryObj = { phoneNumber: digitsOnly };
            } else {
                queryObj = buildNameQueryObj(trimmedValue);
            }

            const queryParamString = UrlBuilderService.getQueryParamsFromObj(queryObj);

            const basePath = isPdxUser ? '/pdxFindPatient' : '/findPatient';

            if (location.pathname === basePath) {
                navigate(`${basePath}${queryParamString}`, { replace: true });
            } else {
                navigate(`${basePath}${queryParamString}`);
            }
            setSearchValue('');
        }
    };

    return (
        <Form className="search-bar-form" inline onSubmit={searchPatient}>
            {!isPdxUser && (
                <FormGroup controlId="search-type" className="search-type-options mb-0">
                    {SEARCH_TYPE_OPTIONS.map((option) => (
                        <Form.Check
                            inline
                            key={option.value}
                            type="radio"
                            name="search-type"
                            id={`search-type-${option.value}`}
                            label={option.label}
                            value={option.value}
                            checked={searchType === option.value}
                            onChange={onSearchTypeChange}
                        />
                    ))}
                </FormGroup>
            )}
            <FormGroup controlId="search-bar" className="mb-0">
                <InputGroup size="sm">
                    <FormControl
                        type="text"
                        value={searchValue}
                        placeholder={SEARCH_TYPE_PLACEHOLDERS[searchType]}
                        onChange={onSearchValue}
                        aria-label={SEARCH_TYPE_PLACEHOLDERS[searchType]}
                    />
                    <InputGroup.Append>
                        <Button variant="primary" size="sm" disabled={!searchValue.trim()} type="submit">
                            Search
                        </Button>
                    </InputGroup.Append>
                </InputGroup>
            </FormGroup>
        </Form>
    );
};

export default SearchBar;
